/**
 * Crew-role display helpers. `crew_roles_needed` (a per-role head-count map set by
 * the supervisor's "Plan the work" studio) is surfaced on the supervisor-facing
 * scheduling screens as "needs 1 Lead · 2 Installers" — the basis for picking /
 * auto-filling the right crew mix. Labels mirror the bid role lines (BidBuilder).
 */
import type { CrewRole, CrewRoleCounts } from '@vari-crew/schemas';

export const CREW_ROLE_LABELS: Record<CrewRole, string> = {
  lead: 'Lead',
  installer: 'Installer',
  driver: 'Driver',
  trainee: 'Trainee',
};

const ROLE_ORDER: CrewRole[] = ['lead', 'installer', 'driver', 'trainee'];

/** Total head count across all roles, or 0 for null. */
export function roleDemandTotal(roles?: CrewRoleCounts | null): number {
  if (!roles) return 0;
  return ROLE_ORDER.reduce((s, r) => s + (roles[r] || 0), 0);
}

/**
 * "1 Lead · 2 Installers" from a role-count map; '' when null or all-zero.
 * Zero-count roles are dropped and labels pluralize.
 */
export function formatRoleDemand(roles?: CrewRoleCounts | null): string {
  if (!roles) return '';
  return ROLE_ORDER.filter((r) => (roles[r] || 0) > 0)
    .map((r) => {
      const n = roles[r];
      const label = CREW_ROLE_LABELS[r];
      return `${n} ${n === 1 ? label : `${label}s`}`;
    })
    .join(' · ');
}
